/**
 * Custom Select Component
 * Provides an accessible custom select dropdown that replaces native select elements
 * with a fully keyboard and screen reader accessible alternative.
 */

(function () {
  'use strict';
  /**
   * Selects an option in a custom select component
   * @param {HTMLElement} option - The option element to select
   */
  function selectOption(option) {
    if (!option) return;

    // Find the root wrapper from the option element
    const root = option.closest('.rs-custom-select__wrapper');
    if (!root) return;

    const select = root.querySelector('.rs-custom-select');
    if (!select) return;

    const button = root.querySelector('.rs-custom-select__button');
    const menu = root.querySelector('.rs-custom-select__menu');
    const options = Array.from(root.querySelectorAll('.rs-custom-select__option'));
    const valueDisplay = root.querySelector('.rs-custom-select__value');
    const hiddenInput = root.querySelector('input[type="hidden"]');
    const isDisabled = select.hasAttribute('data-disabled') || select.dataset.disabled === 'true';

    if (!button || !menu || !valueDisplay || !hiddenInput) return;

    if (isDisabled) return;

    const label = option.querySelector('.rs-custom-select__option-label')?.textContent.trim() || option.textContent.trim();
    const value = option.dataset.value || '';

    // Update display
    valueDisplay.textContent = label;
    valueDisplay.classList.remove('rs-custom-select__value--placeholder');

    // Update hidden input
    hiddenInput.value = value;

    // Update selected state
    options.forEach((opt) => {
      opt.classList.remove('is-selected');
      opt.setAttribute('aria-selected', 'false');
    });
    option.classList.add('is-selected');
    option.setAttribute('aria-selected', 'true');

    // Close menu
    const isOpen = select.classList.contains('open');
    if (isOpen) {
      select.classList.remove('open');
      button.setAttribute('aria-expanded', 'false');
      menu.setAttribute('hidden', '');
      menu.setAttribute('tabindex', '-1');

      // Clear active state and reset tabindex
      options.forEach((opt) => {
        opt.classList.remove('is-active');
        opt.setAttribute('tabindex', '-1');
      });

      button.focus({preventScroll: true});
    }

    // Dispatch change event
    const changeEvent = new CustomEvent('change', {
      bubbles: true,
      detail: {value, label},
    });
    root.dispatchEvent(changeEvent);

    // Also trigger input event on hidden input for form compatibility
    hiddenInput.dispatchEvent(new Event('input', {bubbles: true}));
    hiddenInput.dispatchEvent(new Event('change', {bubbles: true}));
  }

  /**
   * Initializes a custom select component
   * @param {HTMLElement} root - The root element of the custom select
   */
  function initCustomSelect(root) {
    if (!root) return;

    const select = root.querySelector('.rs-custom-select');
    if (!select) return;

    const button = root.querySelector('.rs-custom-select__button');
    const menu = root.querySelector('.rs-custom-select__menu');
    const valueDisplay = root.querySelector('.rs-custom-select__value');
    const hiddenInput = root.querySelector('input[type="hidden"]');
    const isDisabled = select.hasAttribute('data-disabled') || select.dataset.disabled === 'true';

    if (!button || !menu || !valueDisplay || !hiddenInput) return;

    // Read options fresh from the DOM each time — menu contents may be replaced dynamically
    function getOptions() {
      return Array.from(root.querySelectorAll('.rs-custom-select__option'));
    }

    let isOpen = false;
    let activeIndex = Math.max(
      0,
      getOptions().findIndex((opt) => opt.classList.contains('is-selected'))
    );

    /**
     * @returns {HTMLElement[]}
     */
    function getTabbableOptions() {
      return getOptions().filter((opt) => !opt.classList.contains('rs-custom-select__disabled'));
    }

    // Set initial selected value
    const options = getOptions();
    const selectedOption = options.find((opt) => opt.classList.contains('is-selected'));
    if (selectedOption) {
      const selectedLabel = selectedOption.querySelector('.rs-custom-select__option-label')?.textContent.trim() || selectedOption.textContent.trim();
      valueDisplay.textContent = selectedLabel;
      valueDisplay.classList.remove('rs-custom-select__value--placeholder');
      hiddenInput.value = selectedOption.dataset.value || '';
    }

    /**
     * Updates ARIA expanded attribute
     * @param {boolean} expanded - Whether the menu is expanded
     */
    function setAriaExpanded(expanded) {
      button.setAttribute('aria-expanded', String(expanded));
    }

    /**
     * Sets the active option for keyboard navigation
     * @param {number} index - The index of the option to activate
     */
    function setActive(index) {
      const opts = getOptions();
      if (index < 0 || index >= opts.length) return;
      activeIndex = index;

      opts.forEach((opt, idx) => {
        const isActive = idx === index;
        opt.classList.toggle('is-active', isActive);
        opt.setAttribute(
          'tabindex',
          isOpen && !opt.classList.contains('rs-custom-select__disabled') ? '0' : '-1'
        );
        if (isActive) {
          opt.setAttribute('aria-selected', 'true');
        }
      });

      // Scroll active option into view
      const activeOption = opts[index];
      if (activeOption) {
        activeOption.scrollIntoView({block: 'nearest', behavior: 'smooth'});
      }
    }

    /**
     * Opens the select menu
     */
    function openMenu() {
      if (isOpen || isDisabled) return;

      const opts = getOptions();
      isOpen = true;
      select.classList.add('open');
      setAriaExpanded(true);
      menu.removeAttribute('hidden');
      menu.setAttribute('tabindex', '-1');

      opts.forEach((opt) => {
        opt.setAttribute('tabindex', opt.classList.contains('rs-custom-select__disabled') ? '-1' : '0');
      });

      const selectedIndex = opts.findIndex((opt) => opt.classList.contains('is-selected'));
      if (selectedIndex >= 0) {
        activeIndex = selectedIndex;
      }
      if (activeIndex < 0 || activeIndex >= opts.length) {
        activeIndex = 0;
      }

      const tabbable = getTabbableOptions();
      const focusTarget =
        tabbable.find((opt) => opts.indexOf(opt) >= activeIndex) || tabbable[0] || opts[activeIndex] || opts[0];

      if (focusTarget) {
        activeIndex = opts.indexOf(focusTarget);
        setActive(activeIndex);
        focusTarget.focus({preventScroll: true});
      }
    }

    /**
     * Closes the select menu
     * @param {Object} opts - Options for closing
     * @param {boolean} opts.focusButton - Whether to focus the button after closing
     */
    function closeMenu({focusButton = true} = {}) {
      if (!isOpen) return;

      isOpen = false;
      select.classList.remove('open');
      setAriaExpanded(false);
      menu.setAttribute('hidden', '');
      menu.setAttribute('tabindex', '-1');

      getOptions().forEach((opt) => {
        opt.classList.remove('is-active');
        opt.setAttribute('tabindex', '-1');
      });

      if (focusButton) {
        button.focus({preventScroll: true});
      }
    }

    // Button click handler
    button.addEventListener('click', (e) => {
      e.preventDefault();
      if (isDisabled) return;
      if (isOpen) {
        closeMenu();
      } else {
        openMenu();
      }
    });

    // Button keyboard handler
    button.addEventListener('keydown', (e) => {
      if (isDisabled) return;

      const key = e.key;
      if (key === 'ArrowDown' || key === 'Enter' || key === ' ') {
        e.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          if (key === 'ArrowDown') {
            const len = getOptions().length;
            setActive((activeIndex + 1) % len);
          }
        }
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        if (isOpen) {
          const len = getOptions().length;
          setActive((activeIndex - 1 + len) % len);
        } else {
          openMenu();
        }
      } else if (key === 'Escape' && isOpen) {
        e.preventDefault();
        closeMenu();
      }
    });

    // Menu keyboard handler
    menu.addEventListener('keydown', (e) => {
      if (isDisabled) return;

      const key = e.key;

      if (key === 'Escape') {
        e.preventDefault();
        closeMenu();
      } else if (key === 'ArrowDown') {
        e.preventDefault();
        setActive((activeIndex + 1) % getOptions().length);
      } else if (key === 'ArrowUp') {
        e.preventDefault();
        const len = getOptions().length;
        setActive((activeIndex - 1 + len) % len);
      } else if (key === 'Home') {
        e.preventDefault();
        setActive(0);
      } else if (key === 'End') {
        e.preventDefault();
        setActive(getOptions().length - 1);
      } else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        const activeOption = document.activeElement;
        if (activeOption) {
          selectOption(activeOption);
        }
      } else if (key === 'Tab') {
        const active = document.activeElement;
        const tabbable = getTabbableOptions();
        const allOpts = getOptions();
        const currentOption =
          active instanceof Element ? active.closest('.rs-custom-select__option') : null;

        if (currentOption instanceof HTMLElement && currentOption.classList.contains('rs-custom-select__disabled')) {
          const currentIdx = allOpts.indexOf(currentOption);
          if (currentIdx !== -1) {
            const adjacent = !e.shiftKey
              ? tabbable.find((opt) => allOpts.indexOf(opt) > currentIdx)
              : [...tabbable].reverse().find((opt) => allOpts.indexOf(opt) < currentIdx);

            if (adjacent) {
              e.preventDefault();
              e.stopPropagation();
              setActive(allOpts.indexOf(adjacent));
              adjacent.focus({preventScroll: true});
            }
          }
          return;
        }

        const index =
          active instanceof HTMLElement
            ? tabbable.indexOf(active.closest('.rs-custom-select__option') || active)
            : -1;

        if (index === -1) return;

        if (!e.shiftKey && index < tabbable.length - 1) {
          e.preventDefault();
          e.stopPropagation();
          const nextOption = tabbable[index + 1];
          setActive(allOpts.indexOf(nextOption));
          nextOption?.focus({preventScroll: true});
        } else if (e.shiftKey && index > 0) {
          e.preventDefault();
          e.stopPropagation();
          const prevOption = tabbable[index - 1];
          setActive(allOpts.indexOf(prevOption));
          prevOption?.focus({preventScroll: true});
        }
      } else if (key.length === 1) {
        // Type-ahead: find first option starting with the typed character
        e.preventDefault();
        const char = key.toLowerCase();
        const currentTime = Date.now();
        if (!menu.typeAheadCache || currentTime - menu.typeAheadCache.time > 1000) {
          menu.typeAheadCache = {char, time: currentTime};
        } else {
          menu.typeAheadCache.char += char;
        }

        const searchString = menu.typeAheadCache.char;
        const currentOpts = getOptions();
        const matchingIndex = currentOpts.findIndex((opt, idx) => {
          if (idx <= activeIndex) return false;
          const text = (opt.querySelector('.rs-custom-select__option-label')?.textContent || opt.textContent).toLowerCase();
          return text.startsWith(searchString);
        });

        if (matchingIndex >= 0) {
          setActive(matchingIndex);
        } else {
          const firstMatch = currentOpts.findIndex((opt) => {
            const text = (opt.querySelector('.rs-custom-select__option-label')?.textContent || opt.textContent).toLowerCase();
            return text.startsWith(searchString);
          });
          if (firstMatch >= 0) {
            setActive(firstMatch);
          }
        }
      }
    });

    // Option event delegation — handles dynamically replaced options
    menu.addEventListener('click', (e) => {
      const option = e.target.closest('.rs-custom-select__option');
      if (!option || isDisabled) return;
      e.preventDefault();
      selectOption(option);
    });

    menu.addEventListener('mouseenter', (e) => {
      if (!isOpen) return;
      const option = e.target.closest('.rs-custom-select__option');
      if (!option) return;
      const idx = getOptions().indexOf(option);
      if (idx >= 0) setActive(idx);
    }, true);

    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (isOpen && !root.contains(e.target)) {
        closeMenu({focusButton: false});
      }
    });

    // Close menu when focus leaves the component
    root.addEventListener('focusout', () => {
      if (!isOpen) return;

      // Use requestAnimationFrame to check focus after browser processes tab navigation
      // This prevents premature closing when tabbing between options
      requestAnimationFrame(() => {
        // Double-check after a small delay to ensure focus has settled
        setTimeout(() => {
          // Check if focus is still within the component
          const activeElement = document.activeElement;
          if (!root.contains(activeElement)) {
            closeMenu({focusButton: false});
          }
        }, 10);
      });
    });

    // Prevent menu from receiving focus on initial render
    menu.setAttribute('tabindex', '-1');
  }

  /**
   * Initialize all custom select components on the page
   */
  function initAllCustomSelects() {
    const wrappers = document.querySelectorAll('.rs-custom-select__wrapper');
    wrappers.forEach((wrapper) => {
      const select = wrapper.querySelector('.rs-custom-select');
      if (!select) return;

      // Skip if already initialized
      if (select.dataset.initialized === 'true') return;
      select.dataset.initialized = 'true';
      initCustomSelect(wrapper);
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCustomSelects);
  } else {
    initAllCustomSelects();
  }

  // Re-initialize when new content is added (for dynamic content)
  if (typeof window !== 'undefined') {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            // Element node
            const newSelects = node.querySelectorAll ? node.querySelectorAll('.rs-custom-select') : [];
            newSelects.forEach((select) => {
              if (select.dataset.initialized !== 'true') {
                select.dataset.initialized = 'true';
                initCustomSelect(select.closest('.rs-custom-select__wrapper'));
              }
            });

            // Also check if the added node itself is a wrapper or select
            if (node.classList) {
              if (node.classList.contains('rs-custom-select__wrapper')) {
                const select = node.querySelector('.rs-custom-select');
                if (select && select.dataset.initialized !== 'true') {
                  select.dataset.initialized = 'true';
                  initCustomSelect(node);
                }
              } else if (node.classList.contains('rs-custom-select')) {
                if (node.dataset.initialized !== 'true') {
                  node.dataset.initialized = 'true';
                  initCustomSelect(node.closest('.rs-custom-select__wrapper'));
                }
              }
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  window.selectOption = selectOption;
  window.initCustomSelect = initCustomSelect;
  window.initAllCustomSelects = initAllCustomSelects;
})();
